import { KeyAvailableType, PropertyKeyAvailableType } from '@acp/shared';
import {
    isAfter, isEqual, startOfTomorrow,
} from 'date-fns';
import { z } from 'zod';

const propertyKeySchema = z.object({
    onsiteAvailability: z.nativeEnum(PropertyKeyAvailableType),
    temporaryKeyExpirationDate: z.date().nullable(),
    keyOnsiteNotes: z.string().trim(),
}).refine((data) => {
    if (data.onsiteAvailability === PropertyKeyAvailableType.Temporary
        && data.temporaryKeyExpirationDate === null
    ) {
        return false;
    }

    return true;
}, {
    message: 'There must be an expiration date if key is temporarily available',
    path: ['temporaryKeyExpirationDate'],
}).refine((data) => {
    if (data.onsiteAvailability === PropertyKeyAvailableType.Temporary
        && data.temporaryKeyExpirationDate !== null) {
        const tomorrow = startOfTomorrow();
        return isAfter(data.temporaryKeyExpirationDate, tomorrow)
            || isEqual(data.temporaryKeyExpirationDate, tomorrow);
    }
    return true;
}, {
    message: 'Expiration date must be tomorrow or later',
    path: ['temporaryKeyExpirationDate'],
}).refine((data) => {
    if (data.onsiteAvailability !== PropertyKeyAvailableType.Unavailable
        && data.keyOnsiteNotes.length <= 5) {
        return false;
    }

    return true;
}, {
    message: 'Key onsite notes must be at least 5 characters long',
    path: ['keyOnsiteNotes'],
});

const agencyKeySchema = z.object({
    propertyManagerId: z.string().trim().min(1, 'Property manager is required'),
    isKeyAvailable: z.enum(['YES', 'NO']),
    keyNumber: z.string().nullable(),
});

const baseSchema = z.object({
    propertyId: z.coerce.number().int().positive().gt(0)
        .finite(),
    agencyKey: agencyKeySchema.optional(),
    propertyKey: propertyKeySchema.optional(),
});

const editPropertyFormSchema = baseSchema.refine((data) => {
    if (data.agencyKey?.isKeyAvailable === KeyAvailableType.Yes
        && (data.agencyKey?.keyNumber?.trim() === ''
        || data.agencyKey?.keyNumber === undefined
        || data.agencyKey?.keyNumber === null)) {
        return false;
    }

    return true;
}, {
    message: 'Key number is required',
    path: ['agencyKey.keyNumber'],
});

type EditPropertyFormValues = z.infer<
    typeof editPropertyFormSchema
>;

const editPropertyFormSchemaValidKey = baseSchema
    .refine((data) => {
        if (data.agencyKey?.isKeyAvailable !== KeyAvailableType.Yes) {
            return false;
        }

        return true;
    }, {
        message: 'Key must be available to be picked up',
        path: ['agencyKey.isKeyAvailable'],
    }).pipe(editPropertyFormSchema);

export {
    editPropertyFormSchema,
    editPropertyFormSchemaValidKey,
    EditPropertyFormValues,
};
